import { Controller } from "@hotwired/stimulus"

// Learner hot spot block (TMS-758): dots pulse until visited, clicking one
// opens its card (Prev/Next cycles dots, Escape / X / outside click closes),
// the counter tracks explored dots, Show list toggles the accordion view and
// Mark as complete appears once every dot has been visited. Explored state is
// client-side only — nothing is persisted.
export default class extends Controller {
  static targets = [
    "canvas",
    "spot",
    "dot",
    "card",
    "counter",
    "progressFill",
    "listToggle",
    "listToggleIcon",
    "listToggleLabel",
    "list",
    "completeWrap",
    "completeButton"
  ]

  connect() {
    this.visitedIndexes = new Set()
    this.completed = false
  }

  toggleCard(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.spotIndex)

    if (this.openIndex === index) {
      this.closeCards()
    } else {
      this.openCard(index)
    }
  }

  closeCard(event) {
    event?.preventDefault()
    this.closeCards()
  }

  previousSpot(event) {
    event.preventDefault()
    this.stepOpenCard(-1)
  }

  nextSpot(event) {
    event.preventDefault()
    this.stepOpenCard(1)
  }

  closeOnEscape() {
    this.closeCards()
  }

  navigateOnArrowKeys(event) {
    if (this.openIndex === null) return
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return

    event.preventDefault()
    this.stepOpenCard(event.key === "ArrowLeft" ? -1 : 1)
  }

  closeOnOutsideClick(event) {
    if (this.openIndex === null) return
    if (event.target.closest("[data-hot-spot-target='card']")) return
    if (event.target.closest("[data-hot-spot-target='dot']")) return

    this.closeCards()
  }

  toggleList(event) {
    event.preventDefault()
    const showing = this.listTarget.hidden

    this.listTarget.hidden = !showing
    this.listToggleTarget.setAttribute("aria-expanded", String(showing))
    this.listToggleLabelTarget.textContent = showing ? "Hide list" : "Show list"
    this.listToggleIconTarget.classList.toggle("ph-eye", !showing)
    this.listToggleIconTarget.classList.toggle("ph-eye-slash", showing)
  }

  markComplete(event) {
    event.preventDefault()
    if (this.completed) return

    this.completed = true
    this.completeButtonTarget.classList.add("is-completed")
    this.completeButtonTarget.disabled = true
    this.completeButtonTarget.innerHTML =
      '<i class="ph-fill ph-check-circle" aria-hidden="true"></i><span>Completed</span>'
  }

  openCard(index) {
    this.closeCards()

    const dot = this.dotForIndex(index)
    const card = this.cardForIndex(index)
    if (!dot || !card) return

    card.hidden = false
    dot.setAttribute("aria-expanded", "true")
    dot.closest("[data-hot-spot-target='spot']")?.classList.add("is-open")
    this.clampCardWithinCanvas(card)
    card.focus({ preventScroll: true })
    this.markVisited(index)
  }

  closeCards() {
    const openIndex = this.openIndex

    this.cardTargets.forEach((card) => { card.hidden = true })
    this.dotTargets.forEach((dot) => dot.setAttribute("aria-expanded", "false"))
    this.spotTargets.forEach((spot) => spot.classList.remove("is-open"))

    if (openIndex !== null) this.dotForIndex(openIndex)?.focus({ preventScroll: true })
  }

  stepOpenCard(step) {
    const openIndex = this.openIndex
    if (openIndex === null) return

    const total = this.cardTargets.length
    this.openCard((openIndex + step + total) % total)
  }

  markVisited(index) {
    if (this.visitedIndexes.has(index)) return

    this.visitedIndexes.add(index)
    this.dotForIndex(index)?.classList.remove("is-pulsing")
    this.syncProgress()
  }

  syncProgress() {
    const total = this.dotTargets.length
    const visited = this.visitedIndexes.size

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${visited} of ${total} explored`
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${total > 0 ? (visited / total) * 100 : 0}%`
    }
    if (this.hasCompleteWrapTarget && visited === total && total > 0) {
      this.completeWrapTarget.hidden = false
    }
  }

  // Keeps the card horizontally inside the canvas: a dot near the right edge
  // would otherwise push its card out of view.
  clampCardWithinCanvas(card) {
    card.style.marginLeft = ""
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()

    const overflowRight = cardRect.right - canvasRect.right
    const overflowLeft = canvasRect.left - cardRect.left
    if (overflowRight > 0) {
      card.style.marginLeft = `${-overflowRight}px`
    } else if (overflowLeft > 0) {
      card.style.marginLeft = `${overflowLeft}px`
    }
  }

  dotForIndex(index) {
    return this.dotTargets.find((dot) => Number(dot.dataset.spotIndex) === index) || null
  }

  cardForIndex(index) {
    return this.cardTargets.find((card) => Number(card.dataset.spotIndex) === index) || null
  }

  get openIndex() {
    const card = this.cardTargets.find((candidate) => !candidate.hidden)

    return card ? Number(card.dataset.spotIndex) : null
  }
}
